import { OutboundQueueDto } from '@nest/domain-entities-metadata-generated';
import { Skeleton, useValidationResultDialog } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

const OutboundQueueRow = ({ queue, documentTypeId }: { queue: OutboundQueueDto; documentTypeId: number }) => {
	const client = useTerminalClient();
	const navigate = useNavigate();

	const countType: 'documents' | 'quantity' = queue.cumulativeOutbound ? 'quantity' : 'documents';

	const documentsCountQuery = useQuery({
		queryKey: ['outbound-delivery-orders-count-by-queue', queue.id],
		queryFn: () => client.outboundDeliveryOrders.countOutboundDeliveryOrdersByQueueQuery.execute({ queueId: queue.id, documentTypeId: documentTypeId }),
		enabled: !!queue.id && countType === 'documents'
	});

	const quantityCountQuery = useQuery({
		queryKey: ['outbound-delivery-orders-quantity-count-by-queue', queue.id],
		queryFn: () => client.outboundDeliveryOrders.countOutboundDeliveryOrdersQuantityByQueueQuery.execute({ queueId: queue.id, documentTypeId: documentTypeId }),
		enabled: !!queue.id && countType === 'quantity'
	});

	const loading = countType === 'documents' ? documentsCountQuery.isLoading : quantityCountQuery.isLoading;
	const count = countType === 'documents' ? documentsCountQuery.data : quantityCountQuery.data;

	const goToQueue = useCallback(() => {
		if (queue.cumulativeOutbound) {
			navigate(`/outbound/${documentTypeId}/waveless/${queue.id}`);
		} else {
			navigate(`/outbound/${documentTypeId}/queues/${queue.id}`);
		}
	}, [navigate, documentTypeId, queue.cumulativeOutbound, queue.id]);

	return (
		<button
			type="button"
			onClick={goToQueue}
			className="w-full rounded-md border bg-background px-3 py-3 text-left shadow-sm transition-colors hover:bg-muted/50 active:bg-muted"
		>
			<div className="flex items-center justify-between gap-3">
				<span className="truncate text-base font-medium">{queue.name}</span>

				{loading ? (
					<Skeleton className="h-6 w-10 rounded-full" />
				) : (
					<span className="inline-flex h-6 shrink-0 items-center justify-center rounded-full border px-2 text-sm font-semibold">
						{count ?? 0} {countType === 'quantity' ? 'ks' : ''}
					</span>
				)}
			</div>
		</button>
	);
};

export default function OutboundQueuesPage() {
	const documentTypeId = useNumberParam('documentTypeId');
	const client = useTerminalClient();
	const documentType = useQuery({
		queryKey: ['outbound-document-types', documentTypeId],
		queryFn: () => client.outboundDeliveryOrderDocumentTypes.getById.execute({ id: documentTypeId! }),
		enabled: !!documentTypeId
	});

	const outboundQueues = useQuery({
		queryKey: ['outbound-queues', documentTypeId],
		queryFn: () =>
			client.outboundQueues.getAllOutboundQueuesForMeQuery.execute({
				filter: {
					documentType: {
						id: documentTypeId
					}
				}
			}),
		enabled: !!documentTypeId
	});

	useValidationResultDialog([outboundQueues.error, documentType.error]);

	return (
		<TerminalScreen
			showBackButton
			backButtonTo={`/outbound`}
			topBar={
				<div className="flex items-center gap-2">
					<div className="text-sm font-medium text-white/50">Výdej</div>
					<div className="text-sm font-bold">{documentType.data?.name}</div>
				</div>
			}
		>
			<div className="flex flex-col gap-2 p-2">
				{outboundQueues.data ? (
					outboundQueues.data.length === 0 ? (
						<div className="p-6 text-center text-muted-foreground">Žádné fronty</div>
					) : (
						outboundQueues.data.map(queue => <OutboundQueueRow key={queue.id} queue={queue} documentTypeId={documentTypeId!} />)
					)
				) : (
					<>
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</>
				)}
			</div>
		</TerminalScreen>
	);
}
